import React, { useState, useEffect } from "react";
import { format, startOfMonth } from "date-fns";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";

import GetAppIcon from "@material-ui/icons/GetApp";
import SearchIcon from "@material-ui/icons/Search";
import QueryBuilderIcon from "@material-ui/icons/QueryBuilder";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filterPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  cardRoot: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
  },
  cardIcon: {
    fontSize: 48,
    marginRight: theme.spacing(2),
  },
  kpiTime: {
    color: "#25D366",
  },
  kpiClients: {
    color: "#0288d1",
  },
  kpiTickets: {
    color: "#7b1fa2",
  },
  exportBtn: {
    backgroundColor: "#2e7d32",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1b5e20",
    },
    fontWeight: "bold",
  },
  tabHeader: {
    marginBottom: theme.spacing(1),
    borderBottom: "1px solid #e0e0e0",
  },
  badgeSuccess: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    padding: "4px 8px",
    borderRadius: 4,
    fontWeight: "bold",
    display: "inline-block",
  },
}));

const AttendanceReport = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(
    format(startOfMonth(new Date()), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [status, setStatus] = useState("closed");
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState(0);

  const [reportData, setReportData] = useState({
    tickets: [],
    summaryByContact: [],
    totals: {
      totalTickets: 0,
      totalSeconds: 0,
      totalFormatted: "0m",
      totalHoursDecimal: 0,
      totalContacts: 0,
    },
  });

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/attendance", {
        params: {
          startDate,
          endDate,
          status,
          searchParam,
        },
      });
      setReportData(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportCsv = () => {
    try {
      const bom = "\uFEFF";
      let csvContent = "";

      if (tab === 0) {
        csvContent = "Cliente;Telefono;Nro_Asistencias;Total_Tiempo;Horas_Facturables_Decimal\r\n";
        reportData.summaryByContact.forEach((c) => {
          const hoursDecStr = c.totalHoursDecimal.toString().replace(".", ",");
          csvContent += `"${c.contactName}";"${c.contactNumber}";${c.totalTickets};"${c.totalFormatted}";"${hoursDecStr}"\r\n`;
        });
      } else {
        csvContent =
          "Ticket_ID;Cliente;Telefono;Tecnico;Departamento;Fecha_Inicio;Fecha_Cierre;Tiempo_Dedicado;Horas_Decimales;Ultimo_Mensaje\r\n";
        reportData.tickets.forEach((t) => {
          const hoursDecStr = t.durationHoursDecimal.toString().replace(".", ",");
          const createdStr = t.createdAt ? format(new Date(t.createdAt), "dd/MM/yyyy HH:mm") : "";
          const closedStr = t.closedAt ? format(new Date(t.closedAt), "dd/MM/yyyy HH:mm") : "";
          const cleanLastMsg = (t.lastMessage || "").replace(/[\r\n]+/g, " ").replace(/"/g, '""');

          csvContent += `${t.ticketId};"${t.contactName}";"${t.contactNumber}";"${t.userName}";"${t.queueName}";"${createdStr}";"${closedStr}";"${t.durationFormatted}";"${hoursDecStr}";"${cleanLastMsg}"\r\n`;
        });
      }

      const blob = new Blob([bom + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      const filename =
        tab === 0
          ? `facturacion_clientes_${startDate}_al_${endDate}.csv`
          : `detalle_intervenciones_${startDate}_al_${endDate}.csv`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(i18n.t("attendanceReport.toasts.downloadSuccess"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <MainHeader>
        <Title>{i18n.t("attendanceReport.title")}</Title>
      </MainHeader>

      <Paper className={classes.filterPaper} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label={i18n.t("attendanceReport.filters.from")}
              type="date"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label={i18n.t("attendanceReport.filters.to")}
              type="date"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">{i18n.t("attendanceReport.filters.status")}</InputLabel>
              <Select
                labelId="status-select-label"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="closed">{i18n.t("attendanceReport.filters.onlyClosed")}</MenuItem>
                <MenuItem value="all">{i18n.t("attendanceReport.filters.allTickets")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label={i18n.t("attendanceReport.filters.searchLabel")}
              placeholder={i18n.t("attendanceReport.filters.searchPlaceholder")}
              fullWidth
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && fetchReport()}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={fetchReport}
              disabled={loading}
              startIcon={<SearchIcon />}
            >
              {i18n.t("attendanceReport.filters.filterButton")}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              fullWidth
              className={classes.exportBtn}
              onClick={handleExportCsv}
              disabled={loading || reportData.tickets.length === 0}
              startIcon={<GetAppIcon />}
            >
              {i18n.t("attendanceReport.filters.exportButton")}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* KPI Cards */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={4}>
          <div className={classes.cardRoot}>
            <QueryBuilderIcon className={`${classes.cardIcon} ${classes.kpiTime}`} />
            <div>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("attendanceReport.kpis.totalTime")}
              </Typography>
              <Typography variant="h5" style={{ fontWeight: "bold" }}>
                {reportData.totals.totalFormatted}
                <span style={{ fontSize: "0.85rem", color: "#666", marginLeft: 8 }}>
                  ({reportData.totals.totalHoursDecimal.toFixed(2)} {i18n.t("attendanceReport.kpis.hoursUnit")})
                </span>
              </Typography>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={4}>
          <div className={classes.cardRoot}>
            <PeopleAltIcon className={`${classes.cardIcon} ${classes.kpiClients}`} />
            <div>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("attendanceReport.kpis.clientsServed")}
              </Typography>
              <Typography variant="h5" style={{ fontWeight: "bold" }}>
                {reportData.totals.totalContacts} {i18n.t("attendanceReport.kpis.clientsUnit")}
              </Typography>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={4}>
          <div className={classes.cardRoot}>
            <CheckCircleOutlineIcon className={`${classes.cardIcon} ${classes.kpiTickets}`} />
            <div>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("attendanceReport.kpis.interventions")}
              </Typography>
              <Typography variant="h5" style={{ fontWeight: "bold" }}>
                {reportData.totals.totalTickets} {i18n.t("attendanceReport.kpis.attendancesUnit")}
              </Typography>
            </div>
          </div>
        </Grid>
      </Grid>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.tabHeader}>
          <Tabs
            value={tab}
            onChange={(e, val) => setTab(val)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={i18n.t("attendanceReport.tabs.summary")} />
            <Tab label={i18n.t("attendanceReport.tabs.detail")} />
          </Tabs>
        </div>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : tab === 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("attendanceReport.summaryTable.client")}</TableCell>
                <TableCell align="center">{i18n.t("attendanceReport.summaryTable.phone")}</TableCell>
                <TableCell align="center">{i18n.t("attendanceReport.summaryTable.ticketsCount")}</TableCell>
                <TableCell align="center">{i18n.t("attendanceReport.summaryTable.totalTime")}</TableCell>
                <TableCell align="center">{i18n.t("attendanceReport.summaryTable.billableHours")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.summaryByContact.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {i18n.t("attendanceReport.summaryTable.noData")}
                  </TableCell>
                </TableRow>
              ) : (
                reportData.summaryByContact.map((c) => (
                  <TableRow key={c.contactId} hover>
                    <TableCell style={{ fontWeight: "bold" }}>
                      {c.contactName}
                    </TableCell>
                    <TableCell align="center">{c.contactNumber}</TableCell>
                    <TableCell align="center">
                      <span className={classes.badgeSuccess}>
                        {c.totalTickets} {c.totalTickets === 1 ? i18n.t("attendanceReport.summaryTable.ticketUnit") : i18n.t("attendanceReport.summaryTable.ticketsUnit")}
                      </span>
                    </TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold" }}>
                      {c.totalFormatted}
                    </TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold", color: "#2e7d32" }}>
                      {c.totalHoursDecimal.toFixed(2)} h
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("attendanceReport.detailTable.ticket")}</TableCell>
                <TableCell>{i18n.t("attendanceReport.detailTable.client")}</TableCell>
                <TableCell>{i18n.t("attendanceReport.detailTable.technician")}</TableCell>
                <TableCell align="center">{i18n.t("attendanceReport.detailTable.start")}</TableCell>
                <TableCell align="center">{i18n.t("attendanceReport.detailTable.resolution")}</TableCell>
                <TableCell align="center">{i18n.t("attendanceReport.detailTable.duration")}</TableCell>
                <TableCell align="center">{i18n.t("attendanceReport.detailTable.billableHours")}</TableCell>
                <TableCell>{i18n.t("attendanceReport.detailTable.lastMessage")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.tickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {i18n.t("attendanceReport.detailTable.noData")}
                  </TableCell>
                </TableRow>
              ) : (
                reportData.tickets.map((t) => (
                  <TableRow key={t.ticketId} hover>
                    <TableCell>#{t.ticketId}</TableCell>
                    <TableCell style={{ fontWeight: "bold" }}>
                      {t.contactName}
                      <Typography variant="caption" display="block" color="textSecondary">
                        {t.contactNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{t.userName}</TableCell>
                    <TableCell align="center">
                      {t.createdAt ? format(new Date(t.createdAt), "dd/MM/yyyy HH:mm") : "-"}
                    </TableCell>
                    <TableCell align="center">
                      {t.closedAt ? format(new Date(t.closedAt), "dd/MM/yyyy HH:mm") : "-"}
                    </TableCell>
                    <TableCell align="center" style={{ fontWeight: "bold" }}>
                      {t.durationFormatted}
                    </TableCell>
                    <TableCell align="center" style={{ color: "#2e7d32", fontWeight: "bold" }}>
                      {t.durationHoursDecimal.toFixed(2)} h
                    </TableCell>
                    <TableCell style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.lastMessage || "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default AttendanceReport;
